import { Component, Vue, Watch } from "vue-property-decorator";
import { Breadcrumb, Input, Tooltip } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { BuilderPageType } from "@/util/enum";
import { fmtMsg, GLUtil, MatIcon } from "commonui";
import { Paths } from "@/util/paths";
import { Conversation } from "../../conversation-builder.model";
import { selectElementContents } from "../../conversation-builder-util";
import ConversationTemplateState from "@/modules/conversation-template";
import { ConversationBreadcrumbLocale, ConversationBuilderLocale } from "@/locales/localeid";
import { debounce } from "lodash";
import TitleListState from "@/modules/title-list";

@Component({
	components: {
		Breadcrumb,
		BreadcrumbItem: Breadcrumb.Item,
		Input,
		MatIcon,
		Tooltip
	}
})
export default class ConversationBreadcrumb extends Vue {
	private conversationStore = getModule(ConversationBuilderState);
	private templateStore = getModule(ConversationTemplateState);
	private titleListStore = getModule(TitleListState);
	private pageType: BuilderPageType | null = null;
	public locale = ConversationBuilderLocale;

	public conversationName: string = "";
	public skipConversationNameBinding: boolean = false;

	public $refs!: {
		conversationNameControl: HTMLSpanElement;
	};

	created() {
		this.pageType = this.$route.params.pagetype as BuilderPageType;
		this.conversationNameUpdate();
	}

	mounted() {
		if (!this.conversation.id) {
			selectElementContents(this.$refs.conversationNameControl);
			this.$refs.conversationNameControl.focus();
		}
	}

	@Watch("conversationStore.conversationProperties.name")
	private conversationNameUpdate() {
		if (!this.skipConversationNameBinding) {
			this.conversationName = this.conversation.name;
		}
		this.skipConversationNameBinding = false;
	}

	get breadCrumbs(): IBreadcrumbItem[] {
		const items: IBreadcrumbItem[] = [];
		const { title, exposureSequenceInMethodology } = this.conversationStore.conversationExtraInfo;
		if (this.pageType === BuilderPageType.ContentInfo) {
			items.push({ title: title as string, onClick: this.onTitleClick });
		} else if (this.pageType === BuilderPageType.ExposureInfo) {
			items.push({
				title: fmtMsg(ConversationBreadcrumbLocale.ConversationExposureText, {
					0: exposureSequenceInMethodology
				}),
				onClick: this.onTitleClick
			});
		} else if (this.pageType === BuilderPageType.MasterInfo) {
			items.push({
				title: fmtMsg(ConversationBreadcrumbLocale.ConversationMasterText),
				onClick: this.onTitleClick
			});
		}

		// #TODO handle unit case
		// items.push({ title: "Conversation" });
		return items;
	}

	public get conversation(): Conversation {
		return this.conversationStore.conversationProperties;
	}

	onTitleClick() {
		const { parentContentId } = this.conversationStore.conversationExtraInfo;

		if (this.pageType === BuilderPageType.ContentInfo) {
			const contentId = this.$route.params.id;
			parentContentId && this.setSelectedChapterId(contentId); // set chapter id in case of chapter's conversation
			this.$router.push(
				GLUtil.pathStringify(Paths.EditText, { id: parentContentId ? parentContentId : contentId })
			);
		} else if (this.pageType === BuilderPageType.ExposureInfo) {
			const contentId = this.$route.params.contentId;
			parentContentId && this.setSelectedChapterId(contentId); // set chapter id in case of chapter's conversation
			this.$router.push(
				GLUtil.pathStringify(Paths.ContentExposure, { id: parentContentId ? parentContentId : contentId })
			);
		} else if (this.pageType === BuilderPageType.MasterInfo) {
			if (this.$route.params.id) {
				// Conversations will be refreshed when set to empty.
				this.templateStore.setConversations([]);
			}
			this.$router.push(Paths.Conversations);
		}
		// #TODO handle unit case
	}

	private setSelectedChapterId(id: string) {
		// set selected chapter before moving to objective or content page
		this.titleListStore.setEditeContentSelectedChapterId(id);
	}

	public onConversationNameChange(event: InputEvent): void {
		this.skipConversationNameBinding = true;
		const input = event.target! as HTMLSpanElement;
		!this.conversationStore.changedSinceLastSave && this.conversationStore.setChangedSinceLastSave(true);
		this.conversationStore.setConversationProps({ name: input.innerText });
		if (input.innerText) {
			this.dCheckNameUniqueness();
			this.conversationStore.isNameLengthValid(this.conversationStore.conversationProperties.name);
			this.conversationStore.isNameValid(this.conversationStore.conversationProperties.name);
		} else {
			this.conversationStore.setIsConvNameUnique(false);
			this.conversationStore.setIsConvNameLengthValid(true);
			this.conversationStore.setIsConvNameValid(false);
		}
	}

	dCheckNameUniqueness = debounce(this.checkNameUniqueness, 600);
	public checkNameUniqueness() {
		this.conversationStore.isNameUnique(this.conversationStore.conversationProperties.name);
	}

	public onConversationNameClick(): void {
		this.conversationStore.setSelectNode({ id: null });
	}
	public onConversationNameBlur(event: Event): void {
		this.$refs.conversationNameControl.scrollLeft = 0;
	}
}

export interface IBreadcrumbItem {
	title: string;
	onClick?: (a?: any) => void;
	isConversationInput?: boolean;
}
